import React, { useContext, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import mapboxgl from "mapbox-gl";
import { Module } from "Components/Module";
import { ControlPosition, useMapbox, useSearchGeofenceMapFeatures } from "Hooks";
import { brandingSelector } from "Utils/selectors";
import { getLocationMarkerIcon } from "Utils/mapbox";
import { useFavoriteCollections } from "Components/Favorite/useFavoriteCollections";
import { MapGeofenceTooltip, MapTooltip } from "../MapTooltip";
import { SearchContext } from "../Main/SearchContextProvider";

const defaultCityZoom = 12;

interface MapModuleProps {
   className?: string;
}

interface MarketInfo {
   venueId: Guid,
   marker: mapboxgl.Marker
}

const mapControlPosition: ControlPosition = "bottom-right";
const fullScreenControlPosition: ControlPosition = "top-right";

export const MapModule: React.FC<MapModuleProps> = ({ className }) => {
   const branding = useSelector(brandingSelector);
   const searchManager = useContext(SearchContext);
   const markers = useRef<MarketInfo[]>([]);
   const geoLocationMarker = useRef<mapboxgl.Marker | undefined>(undefined);
   const { mapContainerRef, getMapbox, addMarker } = useMapbox({
      controlPosition: mapControlPosition
   });
   const favoritesSlice = useFavoriteCollections();

   const renderMarkers = (fitBounds = true) => {
      markers.current.forEach(markerInfo => markerInfo.marker.remove());
      const newMarkers = searchManager.locations
         .filter(venue => venue.latitude != null && venue.longitude != null)
         .map(venue => {
            const markerIcon = getLocationMarkerIcon(venue, favoritesSlice.data);
            return {
               venueId: venue.venueId,
               marker: addMarker({
                  icon: markerIcon,
                  className: markerIcon == "Preferred" ? "alt-color" : undefined,
                  lat: venue.latitude!,
                  lng: venue.longitude!,
                  hqBrandingOptions: {
                     color: branding?.HqPinColor,
                     colorHover: branding?.HqPinColorHover,
                     logo: branding?.HqPinLogo,
                     label: branding?.HqPinLabel
                  },
                  renderTooltip: () => <MapTooltip venue={venue} />
               })
            };
         });

      const bounds = newMarkers.reduce((acc, markerInfo) => {
         markerInfo.marker.addTo(getMapbox())
         return acc.extend(markerInfo.marker.getLngLat());
      }, new mapboxgl.LngLatBounds());

      searchManager.config.geofences!
         .filter(hq => searchManager.locations.every(location => location.venueId != hq.venueId))
         .map(hq => ({
            venueId: hq.venueId,
            marker: addMarker({
               icon: "HQ",
               className: undefined,
               lat: hq.latitude!,
               lng: hq.longitude!,
               hqBrandingOptions: {
                  color: branding?.HqPinColor,
                  colorHover: branding?.HqPinColorHover,
                  logo: branding?.HqPinLogo,
                  label: branding?.HqPinLabel
               },
               renderTooltip: () => <MapGeofenceTooltip hq={hq} />
            })
         }))
         .forEach(markerInfo => {
            markerInfo.marker.addTo(getMapbox());
            newMarkers.push(markerInfo);
         });

      if (fitBounds && !bounds.isEmpty()) {
         getMapbox().fitBounds(bounds, { padding: { top: 125, right: 45, bottom: 45, left: 45 }, maxZoom: 18 });
      }
      markers.current = newMarkers;
   }

   useEffect(() => {
      const abortController = new AbortController();
      getMapbox();

      return function cleanup() {
         abortController.abort();
      }
   }, []);

   useEffect(() => {
      renderMarkers();
   }, [searchManager.locations]);

   useEffect(() => {
      renderMarkers(false);
   }, [favoritesSlice.data]);

   useEffect(() => {
      if (geoLocationMarker.current) {
         geoLocationMarker.current.remove();
      }
      if (searchManager.geoLocation && (searchManager.geoLocation.latitude || searchManager.geoLocation.longitude)) {
         const newMarker = addMarker({
            icon: "GeoLocation",
            lat: searchManager.geoLocation.latitude,
            lng: searchManager.geoLocation.longitude,
         });
         newMarker.addTo(getMapbox());
         geoLocationMarker.current = newMarker;

         if (!searchManager.locations.length) {
            getMapbox().setCenter(newMarker.getLngLat());
            getMapbox().setZoom(defaultCityZoom);
         }
      }
      else {
         geoLocationMarker.current = undefined;
      }

   }, [searchManager.geoLocation])

   useSearchGeofenceMapFeatures(getMapbox, searchManager.config.geofences!);
   useEffect(() => {
      const map = getMapbox();
      map.addControl(new mapboxgl.FullscreenControl(), fullScreenControlPosition);
   }, []);

   return (<Module className="module-map" fluid>
      <div ref={mapContainerRef} className={classNames("portal-map", className)}></div>
   </Module>);
}